import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, combineLatest, debounceTime, distinctUntilChanged, of, switchMap, tap } from 'rxjs';

import { NominationService } from './nomination.service';
import { Nomination } from '../models/nomination';
import { PagedList } from '../models/paged-list';
import { PagingOptions } from '../models/paged-options';

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const emptyResult: PagedList<Nomination> = { pageNumber: 1, pageSize: 10, totalCount: 0, totalPages: 0, items: [] };

@Injectable({
  providedIn: 'root',
})
export class NominationListService {
  private readonly searchTermSubject = new BehaviorSubject<string>('');
  private readonly statusesSubject = new BehaviorSubject<string[]>([]);
  private readonly pagingRequestSubject = new BehaviorSubject<PagingOptions>({ pageNumber: 1, pageSize: 10 });
  private readonly searchResultSubject = new BehaviorSubject<PagedList<Nomination>>(emptyResult);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly knownStatuses = new Set<string>();
  private readonly knownStatusesSubject = new BehaviorSubject<string[]>([]);

  searchResult$: Observable<PagedList<Nomination>> = this.searchResultSubject.asObservable();
  loading$: Observable<boolean> = this.loadingSubject.asObservable();
  knownStatuses$: Observable<string[]> = this.knownStatusesSubject.asObservable();

  set searchTerm(term: string) {
    this.searchTermSubject.next(term);
  }

  set statuses(statuses: string[]) {
    this.statusesSubject.next(statuses);
  }

  set pagingRequest(pagingRequest: PagingOptions) {
    this.pagingRequestSubject.next(pagingRequest);
  }

  constructor(private nominationService: NominationService) {
    combineLatest([
      this.searchTermSubject.pipe(debounceTime(300), distinctUntilChanged()),
      this.statusesSubject.pipe(distinctUntilChanged(arraysEqual)),
      this.pagingRequestSubject,
    ])
      .pipe(
        tap(() => this.loadingSubject.next(true)),
        switchMap(([searchTerm, statuses, pagingRequest]) =>
          this.nominationService.searchNominations(searchTerm, statuses, pagingRequest).pipe(
            catchError((err) => {
              // Caught here (inside switchMap) rather than in the outer subscribe, so a single
              // failed request doesn't terminate this singleton service's only subscription and
              // silently kill every future refresh/search for the lifetime of the app.
              console.error('Error searching nominations:', err);
              return of({ ...pagingRequest, totalCount: 0, totalPages: 0, items: [] });
            })
          )
        )
      )
      .subscribe((result) => {
        result.items.forEach((n) => {
          if (n.status) {
            this.knownStatuses.add(n.status);
          }
        });
        this.knownStatusesSubject.next(Array.from(this.knownStatuses).sort());
        this.searchResultSubject.next(result);
        this.loadingSubject.next(false);
      });
  }

  /** Re-issues the search with whatever criteria are currently set. */
  refresh(): void {
    this.pagingRequestSubject.next(this.pagingRequestSubject.value);
  }
}
